import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { showToast } from '../../components/ui/toast.utils';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { usePlatformAccessLevel } from '../platformPermissions/usePlatformAccessLevel';
import {
  createInterestedParty,
  deactivateInterestedParty,
  fetchInterestedParties,
  fetchSourceRiskLinks,
  promoteInterestedParty,
  updateInterestedParty,
} from './contextApi';
import type { InterestedParty } from './types';
import { InterestedPartyFormModal } from './InterestedPartyFormModal';

const TYPE_LABEL = { interna: 'Interna', externa: 'Externa' } as const;
const INFLUENCE_LABEL = { alta: 'Alta', media: 'Media', baja: 'Baja' } as const;

interface InterestedPartiesTabProps {
  canEdit: boolean;
}

export function InterestedPartiesTab({ canEdit }: InterestedPartiesTabProps) {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const { canEdit: canEditRisks } = usePlatformAccessLevel('risks.access');
  const [parties, setParties] = useState<InterestedParty[]>([]);
  const [promotedByParty, setPromotedByParty] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InterestedParty | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, links] = await Promise.all([
        fetchInterestedParties(showInactive ? {} : { active: true }),
        fetchSourceRiskLinks(),
      ]);
      setParties(data);
      setPromotedByParty(links.byPartyId);
    } catch {
      setError('No se pudieron cargar las partes interesadas.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (payload: Parameters<typeof createInterestedParty>[0]) => {
    if (editing) {
      await updateInterestedParty(editing.id, payload);
    } else {
      await createInterestedParty(payload);
    }
    await load();
  };

  const handleDeactivate = async (party: InterestedParty) => {
    const ok = await confirm({
      title: 'Dar de baja',
      message: `¿Dar de baja a "${party.name}"? Se conservará el histórico.`,
      confirmText: 'Dar de baja',
      variant: 'danger',
    });
    if (!ok) return;
    await deactivateInterestedParty(party.id);
    await load();
  };

  const handlePromote = async (party: InterestedParty) => {
    setPromotingId(party.id);
    try {
      const risk = await promoteInterestedParty(party.id);
      setPromotedByParty((prev) => ({ ...prev, [party.id]: risk.id }));
      navigate(`/riesgos/${risk.id}`);
    } catch {
      showToast('No se pudo promover a la matriz de riesgos.', 'error');
    } finally {
      setPromotingId(null);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando partes interesadas…</p>;
  }

  if (error) {
    return <p className='text-sm text-destructive'>{error}</p>;
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Mostrar inactivas
        </label>
        {canEdit && (
          <button
            type='button'
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground'
          >
            Nueva parte interesada
          </button>
        )}
      </div>

      <div className='overflow-x-auto rounded-lg border border-border'>
        <table className='min-w-full text-sm'>
          <thead className='bg-muted'>
            <tr>
              <th className='px-4 py-2 text-left font-medium'>Nombre</th>
              <th className='px-4 py-2 text-left font-medium'>Tipo</th>
              <th className='px-4 py-2 text-left font-medium'>Influencia</th>
              <th className='px-4 py-2 text-left font-medium'>Índice</th>
              <th className='px-4 py-2 text-left font-medium'>Necesidades</th>
              <th className='px-4 py-2 text-left font-medium'>Expectativas</th>
              <th className='px-4 py-2 text-left font-medium'>Estado</th>
              {(canEdit || canEditRisks) && (
                <th className='px-4 py-2 text-left font-medium'>Acciones</th>
              )}
            </tr>
          </thead>
          <tbody>
            {parties.length === 0 && (
              <tr>
                <td
                  colSpan={canEdit || canEditRisks ? 8 : 7}
                  className='px-4 py-6 text-center text-muted-foreground'
                >
                  Sin partes interesadas registradas
                </td>
              </tr>
            )}
            {parties.map((party) => {
              const promotedRiskId = promotedByParty[party.id];
              return (
                <tr key={party.id} className='border-t border-border'>
                  <td className='px-4 py-2 font-medium'>{party.name}</td>
                  <td className='px-4 py-2'>{TYPE_LABEL[party.type]}</td>
                  <td className='px-4 py-2'>{INFLUENCE_LABEL[party.influence]}</td>
                  <td className='px-4 py-2'>{party.priorityIndex ?? '—'}</td>
                  <td className='px-4 py-2 max-w-xs truncate' title={party.needs}>
                    {party.needs}
                  </td>
                  <td className='px-4 py-2 max-w-xs truncate' title={party.expectations}>
                    {party.expectations}
                  </td>
                  <td className='px-4 py-2'>
                    <span
                      className={
                        party.active
                          ? 'text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs'
                          : 'text-zinc-600 bg-zinc-200 px-2 py-0.5 rounded text-xs'
                      }
                    >
                      {party.active ? 'Activa' : 'Inactiva'}
                    </span>
                    {promotedRiskId && (
                      <Link
                        to={`/riesgos/${promotedRiskId}`}
                        className='ml-2 text-xs text-primary hover:underline'
                      >
                        Promovida
                      </Link>
                    )}
                  </td>
                  {(canEdit || canEditRisks) && (
                    <td className='px-4 py-2 space-x-2'>
                      {canEdit && (
                        <>
                          <button
                            type='button'
                            onClick={() => {
                              setEditing(party);
                              setModalOpen(true);
                            }}
                            className='text-primary hover:underline text-xs'
                          >
                            Editar
                          </button>
                          {party.active && !promotedRiskId && (
                            <button
                              type='button'
                              onClick={() => void handleDeactivate(party)}
                              className='text-destructive hover:underline text-xs'
                            >
                              Dar de baja
                            </button>
                          )}
                        </>
                      )}
                      {canEdit && canEditRisks && party.active && !promotedRiskId && (
                        <button
                          type='button'
                          disabled={promotingId === party.id}
                          onClick={() => void handlePromote(party)}
                          className='text-primary hover:underline text-xs disabled:opacity-50'
                        >
                          {promotingId === party.id ? 'Promoviendo…' : 'Promover'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <InterestedPartyFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
